import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { marketplace as marketplaceApi } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  ArrowLeft, MapPin, CalendarDays, ShieldCheck, ShoppingBag, Search,
  Leaf, TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';

function BuyDialog({ listing, onBought }) {
  const [openMe, setOpenMe] = useState(false);
  const [form, setForm] = useState({ buyer_name: '', buyer_email: '' });
  const [loading, setLoading] = useState(false);

  const handleBuy = async () => {
    if (!form.buyer_name || !form.buyer_email) { toast.error('Fill in all fields'); return; }
    setLoading(true);
    try {
      await marketplaceApi.buy(listing.listing.id, form);
      toast.success('Booking purchased. Check your email for details.');
      setOpenMe(false);
      onBought();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purchase failed');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={openMe} onOpenChange={setOpenMe}>
      <DialogTrigger asChild>
        <button
          className="l-btn-copper h-11 w-full text-sm inline-flex items-center justify-center gap-2"
          data-testid="marketplace-buy-button"
        >
          <ShoppingBag className="w-4 h-4" /> Take this booking
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[color:var(--l-bg-2)] border border-[color:var(--l-line-2)] text-[color:var(--l-cream)] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="l-display text-xl">Confirm the handover</DialogTitle>
        </DialogHeader>

        <div className="mt-2 p-4 rounded-xl border border-[color:var(--l-line)] bg-[color:var(--l-bg)] text-sm space-y-1">
          <p className="l-display text-base">{listing.hotel?.name}</p>
          <p className="text-[color:var(--l-cream-2)]">
            {listing.room?.name} &mdash; {listing.listing.check_in} to {listing.listing.check_out}
          </p>
          <p className="mt-2 flex items-baseline gap-3">
            <span className="l-display text-2xl text-[color:var(--l-copper)] tabular-nums">${listing.listing.asking_price}</span>
            {listing.listing.discount_pct > 0 && (
              <span className="text-xs text-[color:var(--l-cream-2)]">Save {listing.listing.discount_pct}%</span>
            )}
          </p>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-[13px] text-[color:var(--l-cream-2)] mb-1.5">Your name</label>
            <input
              value={form.buyer_name}
              onChange={(e) => setForm((p) => ({ ...p, buyer_name: e.target.value }))}
              placeholder="John Smith"
              data-testid="marketplace-buyer-name"
              className="l-input w-full px-4 py-3 text-[15px] outline-none"
            />
          </div>
          <div>
            <label className="block text-[13px] text-[color:var(--l-cream-2)] mb-1.5">Your email</label>
            <input
              type="email"
              value={form.buyer_email}
              onChange={(e) => setForm((p) => ({ ...p, buyer_email: e.target.value }))}
              placeholder="you@email.com"
              data-testid="marketplace-buyer-email"
              className="l-input w-full px-4 py-3 text-[15px] outline-none"
            />
          </div>
          <button
            onClick={handleBuy}
            disabled={loading}
            data-testid="marketplace-confirm-buy"
            className="l-btn-copper h-11 w-full text-sm disabled:opacity-60"
          >
            {loading ? 'Processing…' : `Pay $${listing.listing.asking_price} securely`}
          </button>
          <p className="text-[11px] text-center text-[color:var(--l-cream-3)]">
            Secure payment. Booking transfers instantly.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MarketplacePage() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadListings = async () => {
    setLoading(true);
    try {
      const { data } = await marketplaceApi.list('active');
      setListings(data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadListings(); }, []);

  const filtered = search
    ? listings.filter((l) =>
        l.hotel?.name?.toLowerCase().includes(search.toLowerCase()) ||
        l.hotel?.city?.toLowerCase().includes(search.toLowerCase()))
    : listings;

  return (
    <div className="lobby-theme min-h-screen flex flex-col" data-testid="marketplace-page">
      <div className="absolute inset-0 lobby-glow pointer-events-none" />
      <div className="lobby-grain" />

      {/* Nav */}
      <nav className="relative z-10 border-b border-[color:var(--l-line)] bg-[color:var(--l-bg)]/80 backdrop-blur-md sticky top-0">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 text-[color:var(--l-cream-2)] hover:text-[color:var(--l-cream)] transition-colors" data-testid="marketplace-back-home">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to home</span>
          </Link>
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[color:var(--l-copper)] flex items-center justify-center">
              <span className="l-display text-[color:var(--l-bg)] text-xs">b</span>
            </div>
            <span className="l-display text-[15px]">getbitsy</span>
          </Link>
          <Link to="/guest-portal" data-testid="marketplace-my-bookings">
            <button className="l-btn-ghost h-10 px-4 text-sm">My bookings</button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 border-b border-[color:var(--l-line)] py-14 sm:py-20">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8">
          <div className="max-w-2xl lobby-reveal">
            <div className="text-[13px] tracking-wide text-[color:var(--l-cream-3)] mb-3">The marketplace</div>
            <h1 className="l-display text-4xl sm:text-5xl lg:text-6xl">
              Someone&rsquo;s plans<br />
              changed. <span className="text-[color:var(--l-copper)]">Yours just opened up</span>.
            </h1>
            <p className="mt-5 text-[color:var(--l-cream-2)] max-w-xl leading-relaxed">
              Browse bookings that guests couldn&rsquo;t use &mdash; already paid for, instantly transferable, and usually a bit cheaper than the direct rate.
            </p>

            <div className="mt-8 relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[color:var(--l-cream-3)]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by hotel or city…"
                className="l-input w-full pl-11 pr-4 py-3.5 text-[15px] outline-none"
                data-testid="marketplace-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Listings */}
      <section className="relative z-10 flex-1 py-14 sm:py-20">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8">
          {loading ? (
            <div className="text-center py-20 text-[color:var(--l-cream-2)]">Loading stays…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 max-w-md mx-auto">
              <ShoppingBag className="w-10 h-10 text-[color:var(--l-cream-3)] mx-auto mb-4" />
              <h2 className="l-display text-2xl">Nothing right now.</h2>
              <p className="text-[color:var(--l-cream-2)] mt-2 leading-relaxed">
                No transferable bookings at the moment. Check back soon, or list your own if plans changed.
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filtered.map((item) => (
                <div key={item.listing.id} className="l-card overflow-hidden" data-testid="marketplace-listing-card">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={item.hotel?.image_url || `https://picsum.photos/seed/${item.listing.id}/800/600`}
                      alt={item.hotel?.name || 'Hotel'}
                      className="lobby-photo w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--l-bg-2)]/70 via-transparent to-transparent" />
                    {item.listing.discount_pct > 0 && (
                      <div className="absolute top-3 left-3 text-[11px] tracking-wide text-[color:var(--l-bg)] bg-[color:var(--l-copper)] px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                        <TrendingDown className="w-3 h-3" /> {item.listing.discount_pct}% off
                      </div>
                    )}
                    {item.hotel?.verification_status === 'verified' && (
                      <div className="absolute top-3 right-3 text-[11px] tracking-wide text-[color:var(--l-bg)] bg-[color:var(--l-cream)]/90 px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> {item.hotel?.trust_score || 'Verified'}
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="l-display text-lg truncate">{item.hotel?.name || 'Hotel'}</h3>
                    <div className="flex items-center gap-1.5 text-[13px] text-[color:var(--l-cream-3)] mt-1">
                      <MapPin className="w-3.5 h-3.5" /> {item.hotel?.city}, {item.hotel?.country}
                    </div>
                    <div className="flex items-center gap-1.5 text-[13px] text-[color:var(--l-cream-2)] mt-2">
                      <CalendarDays className="w-3.5 h-3.5" /> {item.listing.check_in} &rarr; {item.listing.check_out}
                    </div>
                    {item.room && (
                      <div className="mt-3 inline-block text-[11px] text-[color:var(--l-cream-2)] border border-[color:var(--l-line-2)] rounded-full px-2.5 py-1">
                        {item.room.name}
                      </div>
                    )}
                    <div className="mt-4 flex items-baseline justify-between">
                      <span className="text-[12px] text-[color:var(--l-cream-3)] line-through tabular-nums">${item.listing.original_price}</span>
                      <span className="l-display text-2xl text-[color:var(--l-copper)] tabular-nums">${item.listing.asking_price}</span>
                    </div>
                    <div className="mt-4">
                      <BuyDialog listing={item} onBought={loadListings} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <footer className="relative z-10 border-t border-[color:var(--l-line)] py-6">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 flex items-center justify-center text-[12px] text-[color:var(--l-cream-3)]">
          <span className="inline-flex items-center gap-2">
            <Leaf className="w-3.5 h-3.5 text-[color:var(--l-copper)]" /> Hospitality, quietly rebuilt.
          </span>
        </div>
      </footer>
    </div>
  );
}
